import React, { useState, useEffect } from 'react';
import { useForm, usePage, Link } from '@inertiajs/react';
import { ArrowLeft, Save, Eye, Check, X, Calendar, User, Image as ImageIcon, GripVertical, Plus } from 'lucide-react';
import MobilePreview from '../../components/MobilePreview';

interface PageProps {
  invitation: any;
  whatsappTemplates: any[];
  sendgridSenders: any[];
  events: any[];
  whatsappAccount: any;
}

export default function InvitationsCreate() {
  const { invitation, whatsappTemplates, sendgridSenders, events, whatsappAccount } = usePage<PageProps>().props;

  const { data, setData, post, processing, errors, transform } = useForm({
    name: invitation?.name || '',
    invitation_type: invitation?.invitation_type || 'RSVP Invitation',
    communication_type: 'whatsapp',
    whatsapp_template_sid: invitation?.whatsapp_template_sid || '',
    language: invitation?.language || 'en',
    event_id: invitation?.event_id || '',
    channel: invitation?.channel || '',
    send_at: invitation?.send_at || '',
    whatsapp_reply_enabled: invitation?.whatsapp_reply_enabled ?? true,
    whatsapp_accept_template_sid: invitation?.whatsapp_accept_template_sid || '',
    whatsapp_reject_template_sid: invitation?.whatsapp_reject_template_sid || '',
    acceptance_label: "Yes, I'll attend",
    rejection_label: 'Unable to attend',
    message_body: 'Hello {{1}},\n\nYou are warmly invited to our exclusive event of the year.\n\nThank you!',
    whatsapp_media_enabled: invitation?.whatsapp_media_enabled || true,
    whatsapp_media_variables: invitation?.whatsapp_media_variables || {},
    whatsapp_ticket_qr_size: invitation?.whatsapp_ticket_qr_size || 50,
    whatsapp_ticket_qr_x: invitation?.whatsapp_ticket_qr_x || 0,
    whatsapp_ticket_qr_y: invitation?.whatsapp_ticket_qr_y || 0,
    whatsapp_media_template: null as File | null,
  });

  const [bgPreviewUrl, setBgPreviewUrl] = useState<string>("https://images.unsplash.com/photo-1551818255-e6e10975bc17?q=80&w=1973&auto=format&fit=crop");
  
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setData('whatsapp_media_template', file);
      setBgPreviewUrl(URL.createObjectURL(file));
    }
  };

  const [selectedPlaceholder, setSelectedPlaceholder] = useState<string | null>(null);
  const [localVars, setLocalVars] = useState<any>(invitation?.whatsapp_media_variables || {});
  const [qrState, setQrState] = useState({
    x: invitation?.whatsapp_ticket_qr_x || 0,
    y: invitation?.whatsapp_ticket_qr_y || 0,
    size: invitation?.whatsapp_ticket_qr_size || 50,
    visible: (invitation?.whatsapp_ticket_qr_size || 0) > 0
  });

  // Sync to Inertia form data
  useEffect(() => {
    setData('whatsapp_media_variables', localVars);
  }, [localVars]);

  useEffect(() => {
    setData('whatsapp_ticket_qr_x', qrState.x);
    setData('whatsapp_ticket_qr_y', qrState.y);
    setData('whatsapp_ticket_qr_size', qrState.visible ? qrState.size : 0);
  }, [qrState]);

  const handleAddPlaceholder = (type: string) => {
    if (type === 'qr_code') {
      setQrState(prev => ({ ...prev, x: 50, y: 50, visible: true }));
      setSelectedPlaceholder('qr_code');
      return;
    }
    
    setLocalVars((prev: any) => {
      if (!prev[type]) {
        return {
          ...prev,
          [type]: { type, x: 50, y: 50, font_size: 24, color: '#FFFFFF', weight: 'bold', align: 'left', visible: true }
        };
      }
      return prev;
    });
    setSelectedPlaceholder(type);
  };

  const updateSelected = (key: string, value: any) => {
    if (!selectedPlaceholder) return;
    if (selectedPlaceholder === 'qr_code') {
      if (key === 'size') setQrState(prev => ({ ...prev, size: value }));
      if (key === 'visible') setQrState(prev => ({ ...prev, visible: value }));
      return;
    }
    setLocalVars((prev: any) => ({
      ...prev,
      [selectedPlaceholder]: { ...prev[selectedPlaceholder], [key]: value }
    }));
  };

  const handleMouseDown = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    setSelectedPlaceholder(type);
    
    const startX = e.clientX;
    const startY = e.clientY;
    
    let initialX = type === 'qr_code' ? qrState.x : (localVars[type]?.x || 0);
    let initialY = type === 'qr_code' ? qrState.y : (localVars[type]?.y || 0);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      
      if (type === 'qr_code') {
        setQrState(prev => ({ ...prev, x: Math.max(0, initialX + dx), y: Math.max(0, initialY + dy) }));
      } else {
        setLocalVars((prev: any) => ({
          ...prev,
          [type]: { ...prev[type], x: Math.max(0, initialX + dx), y: Math.max(0, initialY + dy) }
        }));
      }
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const [previewVars, setPreviewVars] = useState({
    name: 'John',
    event: 'Acme Annual Summit 2025'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const locale = window.location.pathname.split('/')[1] || 'en';
    transform((data) => ({ invitation: data }));
    post(`/${locale}/invitations`, { forceFormData: true });
  };

  const currentPreviewBody = data.message_body
    .replace('{{invitee_name}}', previewVars.name)
    .replace('{{event_name}}', previewVars.event);

  return (
    <div className="bg-[#f8f9fa] min-h-screen">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-50 shadow-sm">
        <Link href="/invitations" className="flex items-center text-gray-600 hover:text-gray-900 font-medium">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Link>
        <h1 className="text-xl font-bold text-gray-800">Create WhatsApp Invitation</h1>
        <button className="flex items-center text-gray-600 hover:text-gray-900 border border-gray-300 bg-white rounded-md px-4 py-2 text-sm font-medium shadow-sm transition">
          <Eye className="w-4 h-4 mr-2" />
          Preview
        </button>
      </div>

      <div className="max-w-screen-2xl mx-auto py-8 px-6 flex gap-8">
        
        {/* Main Form Area (Left) */}
        <div className="flex-1 space-y-8">
          
          {/* Section 1: Invitation Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center">
              <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
              <h2 className="text-lg font-semibold text-[#0f766e]">Invitation Details</h2>
            </div>
            
            <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Invitation Name</label>
                <input
                  type="text"
                  placeholder="e.g. VIP Invitation"
                  className={`w-full rounded-md shadow-sm sm:text-sm px-4 py-2.5 bg-gray-50/50 ${errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#0f766e] focus:ring-[#0f766e]'}`}
                  value={data.name}
                  onChange={e => setData('name', e.target.value)}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Event ID</label>
                <select 
                  className={`w-full rounded-md shadow-sm sm:text-sm px-4 py-2.5 bg-gray-50/50 text-gray-800 ${errors.event_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#0f766e] focus:ring-[#0f766e]'}`}
                  value={data.event_id}
                  onChange={e => setData('event_id', e.target.value)}
                >
                  <option value="" disabled>Select event</option>
                  {events?.map(ev => (
                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                  ))}
                </select>
                {errors.event_id && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.event_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">WhatsApp Sender / Channel</label>
                <select 
                  className={`w-full rounded-md shadow-sm sm:text-sm px-4 py-2.5 bg-gray-50/50 text-gray-800 ${errors.channel ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#0f766e] focus:ring-[#0f766e]'}`}
                  value={data.channel}
                  onChange={e => setData('channel', e.target.value)}
                >
                  <option value="" disabled>Select sender or channel</option>
                  {whatsappAccount && (
                    <option value={whatsappAccount.phone_number}>{whatsappAccount.name || whatsappAccount.phone_number} (WhatsApp)</option>
                  )}
                  {sendgridSenders?.map(s => (
                    <option key={s.email} value={s.email}>{s.name} ({s.email})</option>
                  ))}
                </select>
                {errors.channel && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.channel}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Invitation Type</label>
                <select 
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50"
                  value={data.invitation_type}
                  onChange={e => setData('invitation_type', e.target.value)}
                >
                  <option value="Select invitation type" disabled>Select invitation type</option>
                  <option>General Invitation</option>
                  <option>RSVP Invitation</option>
                  <option>Reminder</option>
                  <option>Follow-up</option>
                </select>
                <p className="mt-1.5 text-xs text-gray-400">e.g. General Invitation, RSVP Invitation, Reminder, Follow-up</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Schedule Date / Time (Optional)</label>
                <div className="relative">
                  <input
                    type="datetime-local"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50"
                    value={data.send_at}
                    onChange={e => setData('send_at', e.target.value)}
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-400">Used if the invitation should be sent later</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Created By</label>
                <div className="relative">
                  <input
                    type="text"
                    disabled
                    value="Admin User (you)"
                    className="w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm px-4 py-2.5 pl-10 text-gray-500"
                  />
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Template Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
             <div className="px-6 py-4 border-b border-gray-100 flex items-center">
              <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
              <h2 className="text-lg font-semibold text-[#0f766e]">Template Details</h2>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Template Name</label>
                  <select 
                    className={`w-full rounded-md shadow-sm sm:text-sm px-4 py-2.5 bg-gray-50/50 ${errors.whatsapp_template_sid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#0f766e] focus:ring-[#0f766e]'}`}
                    value={data.whatsapp_template_sid}
                    onChange={e => {
                       setData('whatsapp_template_sid', e.target.value);
                       const template = whatsappTemplates?.find(t => t.template_uuid === e.target.value);
                       if(template && template.body_text) {
                         setData('message_body', template.body_text);
                       }
                    }}
                  >
                    <option value="" disabled>e.g. welcome_message</option>
                    {whatsappTemplates?.map(t => (
                      <option key={t.id} value={t.template_uuid}>{t.template_name}</option>
                    ))}
                  </select>
                  {errors.whatsapp_template_sid && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.whatsapp_template_sid}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
                  <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50">
                    <option>Utility</option>
                    <option>Marketing</option>
                    <option>Authentication</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Header Format</label>
                  <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50">
                    <option>None</option>
                    <option>Text</option>
                    <option>Image</option>
                    <option>Video</option>
                    <option>Document</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message Body</label>
                  <textarea
                    rows={4}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50"
                    value={data.message_body}
                    onChange={e => setData('message_body', e.target.value)}
                  />
                  <p className="mt-1.5 text-xs text-gray-400">Use double brackets for variables. e.g. {'{{invitee_name}}'}, {'{{event_name}}'}, {'{{rsvp_link}}'}, and {'{{qr_code}}'}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Footer Text (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Thank you!"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#0f766e] focus:ring-[#0f766e] sm:text-sm px-4 py-2.5 bg-gray-50/50"
                  />
                </div>
              </div>

              {/* Advanced UI: Placeholder Settings */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex gap-6">
                 
                 {/* Left: Placeholders */}
                 <div className="w-1/4">
                    <h3 className="text-[13px] font-bold text-[#0f766e] mb-1">Placeholder Settings</h3>
                    <p className="text-xs text-gray-500 mb-4 leading-tight">Click placeholders to add them to your template.</p>
                    
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Available Placeholders</h4>
                    <div className="space-y-1.5">
                       {[
                         { name: 'Invitee Name', tag: '{{invitee_name}}', type: 'name' },
                         { name: 'Event Name', tag: '{{event_name}}', type: 'event_name' },
                         { name: 'Organization', tag: '{{organization}}', type: 'organization' },
                         { name: 'Title / Position', tag: '{{title}}', type: 'title' },
                         { name: 'QR Code', tag: '{{qr_code}}', type: 'qr_code' }
                       ].map((item, i) => (
                         <div 
                           key={i} 
                           onClick={() => handleAddPlaceholder(item.type)}
                           className="flex items-center justify-between bg-white border border-gray-200 p-2 rounded cursor-pointer hover:border-[#0f766e] hover:shadow-sm transition shadow-sm text-xs"
                         >
                           <div className="flex items-center text-gray-700">
                             <Plus className="w-3.5 h-3.5 text-[#0f766e] mr-2" />
                             <span className="font-semibold">{item.name}</span>
                           </div>
                         </div>
                       ))}
                    </div>
                 </div>

                 {/* Middle: Template Editor */}
                 <div className="w-2/4 border border-gray-200 rounded-lg bg-gray-50 p-4 flex flex-col shadow-inner">
                    <div className="flex justify-between items-center mb-3">
                       <h3 className="text-sm font-semibold text-gray-700">Template Editor</h3>
                       <label className="cursor-pointer text-xs font-semibold text-[#0f766e] bg-[#0f766e]/10 px-2.5 py-1 rounded hover:bg-[#0f766e]/20 transition flex items-center">
                         <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Change Background
                         <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                       </label>
                    </div>
                    <div 
                      className="relative w-full h-[320px] rounded-md overflow-hidden bg-gray-800 shadow-md"
                      onClick={() => setSelectedPlaceholder(null)}
                    >
                       {/* Background Image */}
                       <img src={bgPreviewUrl} className="w-full h-full object-contain bg-gray-900 select-none pointer-events-none" alt="Ticket bg" />
                       
                       {/* Render text variables */}
                       {Object.entries(localVars).map(([type, config]: [string, any]) => {
                          if (!config.visible) return null;
                          const isSelected = selectedPlaceholder === type;
                          return (
                            <div 
                              key={type}
                              onMouseDown={(e) => handleMouseDown(e, type)}
                              className={`absolute pointer-events-auto cursor-move select-none px-2 py-1 ${isSelected ? 'ring-2 ring-cyan-400 bg-white/20 rounded' : 'hover:ring-1 hover:ring-cyan-400/50 rounded'}`}
                              style={{
                                left: `${config.x}px`,
                                top: `${config.y}px`,
                                fontSize: `${config.font_size}px`,
                                color: config.color,
                                fontWeight: config.weight === 'bold' ? 'bold' : 'normal',
                                fontStyle: config.weight === 'italic' ? 'italic' : 'normal',
                                textAlign: config.align as any,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {'{'}{'{'}{type}{'}'}{'}'}
                            </div>
                          );
                       })}
                       
                       {/* Render QR Code */}
                       {qrState.visible && (
                           <div 
                              onMouseDown={(e) => handleMouseDown(e, 'qr_code')}
                              className={`absolute pointer-events-auto cursor-move select-none bg-white p-1 rounded shadow-sm ${selectedPlaceholder === 'qr_code' ? 'ring-2 ring-cyan-400' : 'hover:ring-1 hover:ring-cyan-400/50'}`}
                              style={{
                                left: `${qrState.x}px`,
                                top: `${qrState.y}px`,
                                width: `${qrState.size + 8}px`,
                                height: `${qrState.size + 8}px`
                              }}
                           >
                             <img src={`https://api.qrserver.com/v1/create-qr-code/?size=${qrState.size}x${qrState.size}&data=example`} className="w-full h-full pointer-events-none" alt="QR" />
                           </div>
                       )}
                    </div>
                    <div className="flex items-center mt-3 text-xs text-gray-500">
                       <span className="w-4 h-4 rounded-full border border-gray-400 flex items-center justify-center mr-1.5 bg-white text-gray-400">i</span>
                       Drag placeholders to position them. Select a placeholder to style it on the right.
                    </div>
                 </div>

                 {/* Right: Styling */}
                 <div className="w-1/4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Placeholder Styling</h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm text-xs min-h-[200px]">
                       {!selectedPlaceholder ? (
                          <div className="text-gray-400 italic text-center mt-10">Select a placeholder on the canvas to edit its styles.</div>
                       ) : (
                         <>
                           <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                              <span className="text-gray-500 font-medium">Selected:</span>
                              <span className="text-[#0f766e] bg-[#0f766e]/10 px-2 py-0.5 rounded font-semibold truncate max-w-[120px] uppercase">
                                {selectedPlaceholder.replace('_', ' ')}
                              </span>
                           </div>
                           
                           <div className="space-y-4">
                              {selectedPlaceholder === 'qr_code' ? (
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600 font-medium">QR Size</span>
                                  <div className="flex items-center">
                                    <input type="number" value={qrState.size} onChange={(e) => updateSelected('size', parseInt(e.target.value) || 10)} className="w-14 text-center border border-gray-300 rounded text-xs py-1 px-1 bg-white focus:border-[#0f766e] focus:ring-[#0f766e]" />
                                    <span className="ml-1 text-gray-500">px</span>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600 font-medium">Font Size</span>
                                    <div className="flex items-center">
                                      <input type="number" value={localVars[selectedPlaceholder]?.font_size || 24} onChange={(e) => updateSelected('font_size', parseInt(e.target.value) || 10)} className="w-14 text-center border border-gray-300 rounded text-xs py-1 px-1 bg-white focus:border-[#0f766e] focus:ring-[#0f766e]" />
                                      <span className="ml-1 text-gray-500">px</span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600 font-medium">Text Color</span>
                                    <div className="flex items-center space-x-2">
                                       <input type="color" value={localVars[selectedPlaceholder]?.color || '#FFFFFF'} onChange={(e) => updateSelected('color', e.target.value)} className="w-6 h-6 border-0 p-0 rounded cursor-pointer" />
                                       <span className="text-gray-500 font-mono uppercase">{localVars[selectedPlaceholder]?.color || '#FFFFFF'}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600 font-medium">Font Style</span>
                                    <div className="flex rounded border border-gray-300 overflow-hidden shadow-sm">
                                       <button onClick={() => updateSelected('weight', localVars[selectedPlaceholder]?.weight === 'bold' ? 'normal' : 'bold')} className={`${localVars[selectedPlaceholder]?.weight === 'bold' ? 'bg-gray-200' : 'bg-white'} px-2.5 py-1 text-gray-800 font-bold border-r border-gray-300 hover:bg-gray-100`}>B</button>
                                       <button onClick={() => updateSelected('weight', localVars[selectedPlaceholder]?.weight === 'italic' ? 'normal' : 'italic')} className={`${localVars[selectedPlaceholder]?.weight === 'italic' ? 'bg-gray-200' : 'bg-white'} px-2.5 py-1 text-gray-800 italic border-gray-300 hover:bg-gray-100`}>I</button>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600 font-medium">Alignment</span>
                                    <div className="flex rounded border border-gray-300 overflow-hidden shadow-sm">
                                       <button onClick={() => updateSelected('align', 'left')} className={`${localVars[selectedPlaceholder]?.align === 'left' ? 'bg-gray-200' : 'bg-white'} px-2 py-1 text-gray-500 border-r border-gray-300 hover:bg-gray-100`}>
                                         <div className="w-3 border-b-2 border-current mb-0.5"></div><div className="w-2 border-b-2 border-current mb-0.5"></div><div className="w-3 border-b-2 border-current"></div>
                                       </button>
                                       <button onClick={() => updateSelected('align', 'center')} className={`${localVars[selectedPlaceholder]?.align === 'center' ? 'bg-gray-200' : 'bg-white'} px-2 py-1 text-gray-800 border-r border-gray-300 flex flex-col items-center hover:bg-gray-100`}>
                                         <div className="w-3 border-b-2 border-current mb-0.5"></div><div className="w-2 border-b-2 border-current mb-0.5"></div><div className="w-3 border-b-2 border-current"></div>
                                       </button>
                                       <button onClick={() => updateSelected('align', 'right')} className={`${localVars[selectedPlaceholder]?.align === 'right' ? 'bg-gray-200' : 'bg-white'} px-2 py-1 text-gray-500 flex flex-col items-end hover:bg-gray-100`}>
                                         <div className="w-3 border-b-2 border-current mb-0.5"></div><div className="w-2 border-b-2 border-current mb-0.5"></div><div className="w-3 border-b-2 border-current"></div>
                                       </button>
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-2">
                                <span className="text-gray-600 font-medium">Visibility</span>
                                <div className="flex items-center">
                                   <button 
                                     onClick={() => updateSelected('visible', selectedPlaceholder === 'qr_code' ? !qrState.visible : !localVars[selectedPlaceholder]?.visible)}
                                     className={`w-8 h-4 rounded-full relative shadow-inner transition-colors ${(selectedPlaceholder === 'qr_code' ? qrState.visible : localVars[selectedPlaceholder]?.visible) ? 'bg-[#0f766e]' : 'bg-gray-300'}`}
                                   >
                                      <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${(selectedPlaceholder === 'qr_code' ? qrState.visible : localVars[selectedPlaceholder]?.visible) ? 'right-0.5' : 'left-0.5'}`}></div>
                                   </button>
                                </div>
                              </div>
                           </div>
                         </>
                       )}
                    </div>
                 </div>

              </div>

            </div>
          </div>

          {/* Section 3: RSVP Configuration */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
             <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition">
              <div className="flex items-center">
                 <div className="bg-[#0f766e] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                 <h2 className="text-lg font-semibold text-[#0f766e]">RSVP Configuration</h2>
              </div>
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400 fill-current"><path d="M7 10l5 5 5-5z"/></svg>
            </div>
            
            <div className="p-6 bg-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Enable RSVP for this invitation</h3>
                  <p className="text-xs text-gray-500 mt-1">If enabled, recipients will receive options to confirm or decline.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={data.whatsapp_reply_enabled} onChange={e => setData('whatsapp_reply_enabled', e.target.checked)} />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0f766e]"></div>
                </label>
              </div>

              {data.whatsapp_reply_enabled && (
                <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                  
                  {/* RSVP Acceptance settings */}
                  <div className="bg-green-50/30 p-5 rounded-lg border border-green-100">
                     <h4 className="font-semibold text-green-700 text-sm mb-4">Acceptance Configuration</h4>
                     <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Follow-up Template</label>
                          <select 
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 sm:text-xs px-3 py-2 bg-white"
                            value={data.whatsapp_accept_template_sid}
                            onChange={e => setData('whatsapp_accept_template_sid', e.target.value)}
                          >
                            <option value="">No follow-up</option>
                            {whatsappTemplates?.map(t => (
                              <option key={t.id} value={t.template_uuid}>{t.template_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Quick Reply Button Label</label>
                          <input
                            type="text"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 sm:text-xs px-3 py-2 bg-white"
                            value={data.acceptance_label}
                            onChange={e => setData('acceptance_label', e.target.value)}
                          />
                          <p className="mt-1.5 text-xs text-gray-400">Examples: Accept, I'll attend, Coming, Confirm Attendance</p>
                        </div>
                     </div>
                  </div>

                  {/* RSVP Rejection settings */}
                  <div className="bg-red-50/30 p-5 rounded-lg border border-red-100">
                     <h4 className="font-semibold text-red-700 text-sm mb-4">Rejection Configuration</h4>
                     <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Follow-up Template</label>
                          <select 
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-600 focus:ring-red-600 sm:text-xs px-3 py-2 bg-white"
                            value={data.whatsapp_reject_template_sid}
                            onChange={e => setData('whatsapp_reject_template_sid', e.target.value)}
                          >
                            <option value="">No follow-up</option>
                            {whatsappTemplates?.map(t => (
                              <option key={t.id} value={t.template_uuid}>{t.template_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Quick Reply Button Label</label>
                          <input
                            type="text"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-600 focus:ring-red-600 sm:text-xs px-3 py-2 bg-white"
                            value={data.rejection_label}
                            onChange={e => setData('rejection_label', e.target.value)}
                          />
                          <p className="mt-1.5 text-xs text-gray-400">Examples: Reject, I cannot attend, Not coming, Decline Invitation</p>
                        </div>
                     </div>
                  </div>

                </div>
              )}
                 
                 <div className="mt-6 flex items-start text-xs text-gray-600 bg-gray-50 p-3 rounded-md border border-gray-200 shadow-sm">
                   <div className="w-4 h-4 rounded-full border border-gray-400 flex items-center justify-center mr-2 flex-shrink-0 bg-white">i</div>
                   <p><strong>RSVP responses are tracked and linked to this invitation and event.</strong> When a user replies via these buttons, their status is automatically updated in the system.</p>
                 </div>
              </div>
            </div>
          
          {/* Action Buttons */}
          <div className="flex gap-4">
             <button
                type="submit"
                onClick={handleSubmit}
                disabled={processing}
                className="bg-gray-800 hover:bg-black text-white font-semibold py-3 px-8 rounded-lg shadow-md transition disabled:opacity-50 text-sm"
              >
                Submit for Approval
             </button>
             <button
                type="button"
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-3 px-8 rounded-lg shadow-sm transition text-sm"
              >
                Cancel
             </button>
          </div>
          
        </div>
        
        {/* Live Preview Sidebar (Right) */}
        <div className="w-[380px] shrink-0">
          <div className="sticky top-28 bg-white border border-gray-100 p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center mb-2">
               <h3 className="font-bold text-[#0f766e] text-lg">Live Preview</h3>
               <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 bg-gray-100 px-2 py-1 rounded">Preview</span>
            </div>
            <p className="text-xs text-gray-500 mb-6">This is how your invitation will appear in WhatsApp.</p>
            
            <div className="flex justify-center transform scale-[0.9] origin-top">
              <MobilePreview 
                headerName="Acme Events"
                messageBody={currentPreviewBody}
                buttons={[data.acceptance_label, data.rejection_label]}
                status="delivered"
              />
            </div>
            
            <div className="mt-6 bg-[#f8f9fa] border border-blue-100 p-3 rounded-lg flex items-start text-xs text-blue-800 shadow-sm">
               <div className="w-4 h-4 rounded-full border border-blue-300 flex items-center justify-center mr-2 flex-shrink-0 bg-white text-blue-500 font-bold">i</div>
               <p>Preview uses sample values for variables. <br/>The final message will use real recipient data.</p>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
}
